import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import {
    StyleSheet,
    View,
    FlatList,
    ActivityIndicator,
    useWindowDimensions,
    TouchableOpacity,
} from "react-native";
import { Card, Checkbox } from "react-native-paper";
import { useTheme } from "@react-navigation/native";
import { createCommonStyles } from "../../../styles/commonStyles";
import CustomLabel from "../../../components/CustomLabel/CustomLabel";
import CustomInput from "../../../components/CustomInput";
import CustomButton from "../../../components/CustomButton";
import { useTranslation } from "react-i18next";
import { useTableHelper } from "../../../hooks/useTableHelper";
import { useSettingsContext } from "../../../hooks/useSettingsContext";
import { useCompany } from "../../../hooks/useCompany";
import { v4 as uuidv4 } from "uuid";
import { showMessage } from "react-native-flash-message";
import Ionicons from "@react-native-vector-icons/ionicons/static";
import Swal from 'sweetalert2';
import useRole from '../../../hooks/userRole';

const SeniorityLevelsListView = React.memo(
    ({ item, index, handleEditPress, styles, t, handleDelete, d365, isAdmin }) => {
        const [isExpanded, setIsExpanded] = useState(false);
        const toggleExpand = () => setIsExpanded(!isExpanded);

        return (
            <View style={styles.container}>
                <View style={styles.row}>
                    <View style={styles.item}>
                        <CustomInput
                            value={item.hcode}
                            setValue={(text) => handleEditPress(index, item, "hcode", text)}
                            editable={true}
                            maxLen={5}
                            type="MAININPUT"
                        />
                    </View>
                    <View style={styles.item}>
                        <CustomInput
                            value={item.description}
                            setValue={(text) => handleEditPress(index, item, "description", text)}
                            editable={true}
                            maxLen={100}
                            type="MAININPUT"
                        />
                    </View>
                    {isAdmin && (
                        <View style={styles.item}>
                            <CustomInput
                                value={item.horder !== undefined && item.horder !== null ? String(item.horder) : ""}
                                setValue={(text) => handleEditPress(index, item, "horder", text)}
                                editable={true}
                                maxLen={20}
                                type="MAININPUT"
                                keyboardType="numeric"
                            />
                        </View>
                    )}
                    {isAdmin && (
                        <View style={styles.item}>
                            <CustomInput
                                value={item.person !== undefined && item.person !== null ? String(item.person) : ""}
                                setValue={(text) => handleEditPress(index, item, "person", text)}
                                editable={true}
                                maxLen={20}
                                type="MAININPUT"
                                keyboardType="numeric"
                            />
                        </View>
                    )}
                    <View style={styles.checkboxContainer}>
                        <Checkbox
                            status={item.isOvertimePaid ? "checked" : "unchecked"}
                            onPress={() =>
                                handleEditPress(index, item, "isOvertimePaid", !item.isOvertimePaid)
                            }
                            color={item.isOvertimePaid ? "#86BC25" : undefined}
                        />
                    </View>
                    <View style={styles.checkboxContainer}>
                        <Checkbox
                            status={item.isOvertimeAllowed ? "checked" : "unchecked"}
                            onPress={() =>
                                handleEditPress(index, item, "isOvertimeAllowed", !item.isOvertimeAllowed)
                            }
                            color={item.isOvertimeAllowed ? "#86BC25" : undefined}
                        />
                    </View>
                    <View style={styles.item}>
                        <CustomInput
                            value={item.pdi_empl_level_code_EMPF}
                            setValue={(text) => handleEditPress(index, item, "pdi_empl_level_code_EMPF", text)}
                            editable={true}
                            maxLen={20}
                            type="MAININPUT"
                        />
                    </View>
                    <View style={styles.item}>
                        <CustomInput
                            value={item.pdi_empl_level_description_EMPF}
                            setValue={(text) => handleEditPress(index, item, "pdi_empl_level_description_EMPF", text)}
                            editable={true}
                            maxLen={20}
                            type="MAININPUT"
                        />
                    </View>
                    <View style={styles.item}>
                        <CustomInput
                            value={item.hcodeDME}
                            setValue={(text) => handleEditPress(index, item, "hcodeDME", text)}
                            editable={true}
                            maxLen={20}
                            type="MAININPUT"
                        />
                    </View>
                    <View style={{ flexDirection: "row", alignItems: "center" }}>
                        <CustomButton
                            iconName="trash-outline"
                            onPress={() => handleDelete(item, index, "rowGuid")}
                            show="HOME_BELL"
                            type="HOME_BELL"
                            textTop="2"
                            sz={30}
                        />
                        {d365 && (
                            <Ionicons
                                name={isExpanded ? "chevron-down-outline" : "chevron-forward-outline"}
                                style={styles.icons}
                                onPress={toggleExpand}
                            />
                        )}
                    </View>
                    {isExpanded && (
                        <View style={styles.item}>
                            <CustomInput
                                value={item.d365Code}
                                setValue={(text) => handleEditPress(index, item, "d365Code", text)}
                                editable={true}
                                maxLen={20}
                                type="MAININPUT"
                            />
                        </View>
                    )}
                </View>
            </View>
        );
    }
);

const HRAdminSeniorityLevelsScreen = (props) => {
    const [seniorityLevels, setSeniorityLevels] = useState([]);
    const [originalSeniorityLevels, setOriginalSeniorityLevels] = useState([]);
    const [changes, setChanges] = useState({});
    const [deletedItems, setDeletedItems] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const ref = useRef(null);
    const { t } = useTranslation();
    const { rowAdd, rowEdit, rowDelete } = useTableHelper();
    const { getSeniorityLevels, createSeniorityLevel, updateSeniorityLevel, deleteSeniorityLevel, } = useCompany();
    const { colors } = useTheme();
    const { width, height } = useWindowDimensions();
    const styles = useMemo(() => makeStyles(colors, width, height), [colors, width, height]);
    const common = createCommonStyles(colors);
    const { selectedCompany, selectedLevel, hcode } = props;
    const { d365 } = useSettingsContext();
    const { isAdmin } = useRole();

    const loadSeniorityLevels = async () => {
        if (!selectedCompany || !hcode) {
            setSeniorityLevels([]);
            setOriginalSeniorityLevels([]);
            return;
        }
        setIsLoading(true);
        try {
            let fetchedLevels = await getSeniorityLevels(selectedCompany, hcode);
            // Add original keys for tracking
            const levelsWithOriginalKeys = fetchedLevels.map(item => ({
                ...item,
                originalHCode: item.hcode,
                horder: item.HOrder ?? item.horder ?? null,
                originalCompany: item.company,
                isOvertimePaid: item.isOvertimePaid ?? false,
                isOvertimeAllowed: item.isOvertimeAllowed ?? false,
            }));
            if (selectedLevel) {
                fetchedLevels = levelsWithOriginalKeys.filter(lvl => lvl.seniority === selectedLevel);
            } else {
                fetchedLevels = levelsWithOriginalKeys;
            }
            setSeniorityLevels(fetchedLevels);
            setOriginalSeniorityLevels(fetchedLevels);
        } catch (error) {
            showMessage({
                message: t("common:ErrorLoadingSeniorityLevels"),
                type: "info",
                duration: 3000,
            });
            setSeniorityLevels([]);
            setOriginalSeniorityLevels([]);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        loadSeniorityLevels();
    }, [props.selectedCompany, hcode]);

    const removeFromChangesAfterSave = () => {
        setChanges({});
        setDeletedItems([]);
    };

    const handleEditPress = useCallback(
        (index, item, key, value) => {
            let sanitizedValue = value;
            if (key === "horder" || key === "person") {
                sanitizedValue = value.replace(/[^0-9]/g, '');
                const originalValue = item[key] !== undefined && item[key] !== null ? String(item[key]) : "";
                if (originalValue === sanitizedValue) return;
            } else {
                if (item[key] === sanitizedValue) return;
            }
            rowEdit(index, item, key, sanitizedValue, changes, setChanges, seniorityLevels, setSeniorityLevels);
        },
        [rowEdit, changes, setChanges, seniorityLevels, setSeniorityLevels]
    );

    const handleCancelChanges = () => {
        setSeniorityLevels(originalSeniorityLevels);
        setChanges({});
        setDeletedItems([]);
        showMessage({
            message: t("common:changesCancelled"),
            type: "info",
            duration: 3000,
        });
    };

    const validateInput = () => {
        for (const change of Object.values(changes)) {
            if (!change.hcode || change.hcode.trim() === "" || change.hcode.length > 5) {
                return [false, t("common:requiredHierarchyCode")];
            }
            if (!change.hcodeDME || change.hcodeDME.trim() === "" || change.hcodeDME.length > 5) {
                return [false, t("common:requiredRegionalCode")];
            }
            if (
                change.horder !== null &&
                change.horder !== "" &&
                (Number(change.horder) < 0 || Number(change.horder) > 32767)
            ) {
                return [false, t("common:invalidOrder")];
            }
            if (change.person !== null && (change.person < 0 || change.person > 32767)) {
                return [false, t("common:invalidPerson")];
            }
        }
        return [true, ""];
    };


    const handleSave = async () => {
        try {
            setIsLoading(true);

            // Handle deletes first
            if (deletedItems.length > 0) {
                for (const item of deletedItems) {
                    if (!item || !item.hcode) continue;
                    const deleteResult = await deleteSeniorityLevel({
                        Company: item.company || selectedCompany,
                        HCode: item.hcode,
                    });
                    if (!deleteResult.success) {
                        if (deleteResult.conflict) {
                            const detailMessage = deleteResult.details?.detail || t("common:seniorityLevelInUseMessage");
                            await Swal.fire({
                                title: t("common:cannotDeleteInUse"),
                                text: detailMessage,
                                icon: "error",
                                confirmButtonText: t("common:ok"),
                            });
                        } else {
                            showMessage({
                                message: t("common:failedDelete"),
                                type: "danger",
                                duration: 3000,
                            });
                        }
                        setIsLoading(false);
                        return;
                    }
                }
                showMessage({
                    message: t("common:senioritySuccessDelete"),
                    type: "success",
                    duration: 3000,
                });
                removeFromChangesAfterSave();
            }

            // Handle creates and updates
            if (Object.entries(changes).length > 0) {
                const [isValid, validationMessage] = validateInput();
                if (!isValid) {
                    showMessage({
                        message: validationMessage,
                        type: "danger",
                        duration: 3000,
                    });
                    setIsLoading(false);
                    return;
                }

                for (const change of Object.values(changes)) {
                    // Determine if existing by matching original keys
                    const isExisting = originalSeniorityLevels.some(
                        (lvl) =>
                            lvl.originalHCode === change.originalHCode &&
                            lvl.originalCompany === change.originalCompany
                    );
                    const req = {
                        HCode: change.hcode || "",
                        Company: change.company || selectedCompany || "",
                        Description: change.description || "",
                        Seniority: change.seniority || hcode || "",
                        HOrder: change.horder !== undefined && change.horder !== "" ? Number(change.horder) : null,
                        Person: change.person !== undefined && change.person !== "" ? Number(change.person) : null,
                        IsOvertimePaid: Boolean(change.isOvertimePaid),
                        IsOvertimeAllowed: Boolean(change.isOvertimeAllowed),
                        InterfaceCode: change.pdi_empl_level_code_EMPF || null,
                        InterfaceDescription: change.pdi_empl_level_description_EMPF || null,
                        RegionalCode: change.hcodeDME || "",
                        OriginalHCode: change.originalHCode,
                        OriginalCompany: change.originalCompany,
                        RowGuid: change.rowguid ?? null,
                    };

                    if (isExisting) {
                        await updateSeniorityLevel(req);
                    } else {
                        await createSeniorityLevel(req);
                    }
                }

                showMessage({
                    message: t("common:senioritySuccessSave"),
                    type: "success",
                    duration: 3000,
                });
                removeFromChangesAfterSave();
            }

            if (deletedItems.length === 0 && Object.entries(changes).length === 0) {
                showMessage({
                    message: t("common:noChanges"),
                    type: "info",
                    duration: 3000,
                });
            }
            await loadSeniorityLevels();
            setIsLoading(false);
        } catch (error) {
            setIsLoading(false);
            const validationErrors = error?.response?.data?.errors || {};
            const messages = Object.values(validationErrors).flat();

            const duplicateError = messages.find(msg =>
                msg.toLowerCase().includes("seniority already exists")
            );
            const genericDuplicateError = messages.some(msg =>
                msg.toLowerCase().includes("duplicate") ||
                msg.toLowerCase().includes("already exists") ||
                msg.toLowerCase().includes("unique constraint")
            );

            if (duplicateError || genericDuplicateError) {
                showMessage({
                    message: t("common:duplicateHierarchyCodeMessage"),
                    type: "danger",
                    duration: 5000,
                });
            } else {
                showMessage({
                    message: t("common:errorSave"),
                    type: "danger",
                    duration: 3000,
                });
            }
        }
    };

    const handleDelete = async (item, index) => {
        if (!item) {
            showMessage({
                message: t("common:invalidItem"),
                type: "danger",
                duration: 3000,
            });
            return;
        }
        if (!item.hcode) {
            setSeniorityLevels(prev => {
                const newLevels = [...prev];
                newLevels.splice(index, 1);
                return newLevels;
            });
            setChanges(prevChanges => {
                const newChanges = { ...prevChanges };
                if (item.rowGuid && newChanges[item.rowGuid]) {
                    delete newChanges[item.rowGuid];
                }
                return newChanges;
            });
            setDeletedItems(prevDeleted => {
                if (!prevDeleted) return prevDeleted;
                return prevDeleted.filter(deletedItem => deletedItem.rowGuid !== item.rowGuid);
            });
            showMessage({
                message: t("common:rowDeletedLocally"),
                type: "success",
                duration: 2000,
            });
            return;
        }
        setDeletedItems(prev => [...(prev || []), item]);
        rowDelete(item, index, "rowGuid", changes, setChanges, seniorityLevels, setSeniorityLevels, setDeletedItems, ref);
    };
    const renderData = useCallback(
        (item, index) => (
            <Card style={[common.cardstyle]}>
                <Card.Content>
                    <SeniorityLevelsListView
                        item={item}
                        index={index}
                        handleEditPress={handleEditPress}
                        styles={styles}
                        t={t}
                        handleDelete={handleDelete}
                        d365={d365}
                        isAdmin={isAdmin}
                    />
                </Card.Content>
            </Card>
        ),
        [seniorityLevels, changes, deletedItems, d365, isAdmin]
    );

    if (isLoading) {
        return (
            <View style={styles.loader}>
                <ActivityIndicator animating={true} color={colors.blue} />
            </View>
        );
    }
    const handleGoBack = () => {
        if (props.onClose) {
            props.onClose();
        }
    };

    return (
        <View style={styles.mainView}>
            <View >
                <TouchableOpacity onPress={handleGoBack} style={styles.backButton}>
                    <Ionicons name="arrow-back" size={30} color={colors.text} />
                </TouchableOpacity>
            </View>
            <View style={styles.container}>
                <View style={styles.buttonsRow}>
                    <View style={styles.leftButtons}>
                        {/* Placeholder for future buttons if needed */}
                    </View>
                    <View style={styles.rightButtons}>
                        <CustomButton
                            text={t("common:new")}
                            onPress={() => {
                                rowAdd(
                                    seniorityLevels,
                                    setSeniorityLevels,
                                    ["rowGuid"],
                                    [],
                                    ref,
                                    t,
                                    [],
                                    [],
                                    changes,
                                    setChanges,
                                    undefined,
                                    undefined,
                                    "rowGuid",
                                    false,
                                    ["date"],
                                    () => uuidv4(),
                                    {
                                        seniority: hcode,
                                        company: selectedCompany,
                                        isOvertimePaid: false,
                                        isOvertimeAllowed: false,
                                    }
                                );
                            }}
                            type="BUTTON_ADD"
                            textTop="0"
                            sz={15}
                        />
                        {(Object.keys(changes).length > 0 || deletedItems.length > 0) && (
                            <CustomButton
                                text={t("common:saveFunction")}
                                onPress={handleSave}
                                type="BUTTON_SAVE"
                                isGradient={true}
                                textTop="0"
                                sz={15}
                            />
                        )}
                        <CustomButton
                            text={t("common:cancel")}
                            onPress={handleCancelChanges}
                            type="BUTTON_CANCEL"
                            textTop="0"
                            sz={15}
                        />
                    </View>
                </View>
                <FlatList
                    data={seniorityLevels}
                    keyExtractor={(item) => String(item.rowGuid)}
                    renderItem={({ item, index }) => renderData(item, index)}
                    refreshing={isLoading}
                    ref={ref}
                    ListHeaderComponent={
                        <View style={common.listTitle}>
                            <View style={styles.row}>
                                <CustomLabel value={t("common:HierarchyCode")} type="SUBTITLE" />
                                <CustomLabel value={t("common:description")} type="SUBTITLE" />
                                {isAdmin && (
                                    <>
                                        <CustomLabel value={t("common:order")} type="SUBTITLE" />
                                        <CustomLabel value={t("common:person")} type="SUBTITLE" />
                                    </>
                                )}
                                <CustomLabel value={t("common:isOvertimePayable")} type="SUBTITLE" />
                                <CustomLabel value={t("common:isOvertimeAllowed")} type="SUBTITLE" />
                                <CustomLabel value={t("common:interfaceCode")} type="SUBTITLE" />
                                <CustomLabel value={t("common:interfaceDescripiton")} type="SUBTITLE" />
                                <CustomLabel value={t("common:regionalCode")} type="SUBTITLE" />
                                <View style={{ width: 40 }} />
                            </View>
                            <View style={common.divider} />
                        </View>
                    }
                    ListEmptyComponent={
                        isLoading ? (
                            <ActivityIndicator animating={true} color={colors.blue} />
                        ) : (
                            <CustomLabel value={t("common:noSeniorityLevel")} type="NO_FUNCTIONS" />
                        )
                    }
                    initialNumToRender={4}
                />
            </View>
        </View>
    );
};

const makeStyles = (colors, width, height) =>
    StyleSheet.create({
        container: {
            flex: 1,
        },
        mainView: {
            flex: 1,
        },
        row: {
            flex: 1,
            flexDirection: "row",
            alignItems: "center",
        },
        item: {
            flex: 1,
            justifyContent: "center",
            marginRight: 6,
        },
        buttonsRow: {
            flexDirection: "row",
            justifyContent: "space-between",
            alignItems: "center",
            marginHorizontal: 15,
            marginVertical: 10,
        },
        leftButtons: {
            flexDirection: "row",
            alignItems: "center",
        },
        rightButtons: {
            flexDirection: "row",
            alignItems: "center",
        },
        loader: {
            flex: 1,
            alignItems: "center",
            justifyContent: "center",
            marginBottom: "4%",
        },
        icons: {
            fontSize: 24,
            marginLeft: 10,
            color: colors.text,
        },
        backButton: {
            alignSelf: 'flex-end',
            margin: 0
        },
        checkboxContainer: {
            justifyContent: "space-between",
            alignItems: "center",
            marginRight: 6,
            flex: 1
        },

    });

export default HRAdminSeniorityLevelsScreen;